import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { ListBox } from 'primereact/listbox';
import AnimateHeight from 'react-animate-height';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import SearchItemTemplate from './SearchItemTemplate';
import mixpanel from '../../libs/Mixpanel';
import {
  ICaseSearchResult,
  ISearchResultTemplateProps,
  TransformSearchResultsToProps,
  searchCases,
} from './BroadSearchDataService';
import './BroadSearch.css';
import './BroadSearchAutoComplete.scss';
import { useDebounce } from '../../libs/useDebounce';
import { ImpersonationRecord, Profile } from '../../shared/types/appTypes';

// Exported for external consumers (kept as named exports for back-compat)
export const Badge = () => <div className='broad-search__badge' />;
export const Caption2 = ({ children }: { children: React.ReactNode }) => (
  <p className='broad-search__caption2'>{children}</p>
);

interface BroadSearchProps {
  toast: RefObject<Toast | null>;
  profile: Profile | null;
  impersonation: ImpersonationRecord | null;
}

const BroadSearchAutoComplete = ({
  toast,
  profile,
  impersonation,
}: BroadSearchProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [caseSuggestions, setCaseSuggestions] = useState<ICaseSearchResult[]>(
    [],
  );
  const [selectedCase, setSelectedCase] =
    useState<ISearchResultTemplateProps | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState<number | string>(0);
  const [serverResponded, setServerResponded] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputTextRef = useRef<HTMLInputElement | null>(null);
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 300);

  // Dismiss and reset the search whenever the user navigates to a different
  // page so stale results from a previous case don't linger in the dropdown.
  // We collapse the dropdown immediately (setHeight/setFocused) but defer
  // clearing the result data until after AnimateHeight's 300ms animation so
  // the "No Cases found" branch never flashes into view during the collapse.
  useEffect(() => {
    setSearchTerm('');
    setSelectedCase(null);
    setFocused(false);
    setHeight(0);
    const timer = setTimeout(() => {
      setCaseSuggestions([]);
      setServerResponded(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    if (debouncedSearchTerm.length > 1) {
      setLoading(true);
      searchCases(debouncedSearchTerm)
        .then((results) => {
          setLoading(false);
          setServerResponded(true);
          height === 0 && setHeight('auto');
          setCaseSuggestions(results);
          mixpanel.track('cases-autocompleter-results-returned', {
            search_phrase: searchTerm,
            totalOptions: results.length,
          });
        })
        .catch((error: { message: string }) => {
          setLoading(false);
          setServerResponded(true);
          toast?.current &&
            toast.current.show({
              severity: 'error',
              summary: 'Could not search',
              detail: `There's been a problem, ${error.message}`,
            });
        });
    } else {
      setCaseSuggestions([] as ICaseSearchResult[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: missing deps are stable API-call functions; adding them would cause infinite re-renders
  }, [debouncedSearchTerm]);

  const escFunction = useCallback((event: { key: string }) => {
    if (event.key === 'Escape') {
      setFocused(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const options =
    caseSuggestions?.length &&
    serverResponded &&
    TransformSearchResultsToProps(caseSuggestions, impersonation || profile);

  // "case" is a reserved word
  const handleCaseSelect = useCallback(
    (_case: ISearchResultTemplateProps | null) => {
      // PrimeReact's ListBox fires onChange with null when it deselects the
      // current item (e.g. when the list collapses). Guard so we never read
      // case_id off null.
      if (!_case) return;
      setSelectedCase(_case);
      if (options) {
        mixpanel.track('cases-autocompleter-click-result', {
          searchPhrase: searchTerm,
          selectedSuggestion: _case,
          options,
          selectionRank:
            (options?.findIndex((value) => value.case_id === _case.case_id) ??
              -1) + 1,
          totalOptions: options.length,
          currentUrl: window.location.pathname,
        });
      }
      // Dismiss the dropdown synchronously before navigating so the overlay
      // doesn't stay painted over the destination page.
      setFocused(false);
      setHeight(0);
      void navigate(`/cases/${_case.case_id}`);
    },
    [navigate, options, searchTerm],
  );

  const handleKeystroke = useCallback(
    (event: { key: string }) => {
      switch (event.key) {
        case 'Enter':
          if (selectedCase) {
            handleCaseSelect(selectedCase);
          }
          break;
        case 'ArrowDown':
          if (options && options.length > 0) {
            const nextIndex =
              options.findIndex(
                (value) => value.case_id === selectedCase?.case_id,
              ) + 1;
            if (nextIndex < options.length) {
              setSelectedCase(options[nextIndex]);
            }
          }
          break;
        case 'ArrowUp':
          if (options && options.length > 0) {
            const nextIndex =
              options.findIndex(
                (value) => value.case_id === selectedCase?.case_id,
              ) - 1;
            if (nextIndex >= 0) {
              setSelectedCase(options[nextIndex]);
            }
          }
          break;
        case 'Escape':
          setFocused(false);
          setHeight(0);
          inputTextRef.current?.blur();
          break;
        default:
      }
    },
    [selectedCase, options, handleCaseSelect],
  );

  const handleBlur = useCallback(() => {
    mixpanel.track('cases-autocompleter-blur', {
      current_page: window.location.pathname,
      search_phrase: searchTerm,
      serverResponded,
    });
    setFocused(false);
    setHeight(0);
  }, [searchTerm, serverResponded]);

  const handleFocus = useCallback(() => {
    mixpanel.track('cases-autocompleter-focus', {
      current_page: window.location.pathname,
      search_phrase: searchTerm,
      serverResponded,
    });
    setFocused(true);
    if (serverResponded) setHeight('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: missing deps are stable API-call functions; adding them would cause infinite re-renders
  }, [serverResponded]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- overlay dismiss is supplementary; Escape key is handled by the input's onKeyDown */}
      <div
        className={`broad-search__overlay ${focused ? 'broad-search__overlay--visible' : 'broad-search__overlay--hidden'}`}
        onClick={handleBlur}
      />
      <div className='broad-search__search-container lg:w-26rem md:m-w-20rem'>
        <span className='broad-search__input-container p-input-icon-left'>
          <i className='pi pi-search' />
          <InputText
            ref={inputTextRef}
            role={'searchbox'}
            placeholder='Search People, Cases'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeystroke}
            onFocus={handleFocus}
            style={{ borderRadius: '8px' }}
            maxLength={200}
          />
          <span
            className={`broad-search__cancel-wrapper ${loading ? 'broad-search__cancel-wrapper--loading' : 'broad-search__cancel-wrapper--idle'}`}
          >
            <Button
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                display: searchTerm.length > 0 ? 'block' : 'none',
              }}
              icon='pi pi-times'
              className='p-button-rounded p-button-text p-button-plain'
              onClick={() => {
                mixpanel.track('cases-autocompleter-cancel', {
                  current_page: window.location.pathname,
                  search_phrase: searchTerm,
                  serverResponded,
                });
                setFocused(false);
                setSearchTerm('');
                setHeight(0);
                setTimeout(() => {
                  setServerResponded(false);
                  setCaseSuggestions([]);
                }, 300);
              }}
            />
          </span>
          <span
            className={`broad-search__spinner-wrapper ${loading ? 'broad-search__spinner-wrapper--loading' : 'broad-search__spinner-wrapper--idle'}`}
          >
            <i className='pi pi-spin pi-spinner' />
          </span>
        </span>
        <AnimateHeight
          id={'animated-search-list'}
          duration={300}
          height={height}
        >
          {options ? (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            <div className='broad-search__results-container' tabIndex={0}>
              <ListBox
                className='broad-search__results-box'
                value={selectedCase}
                itemTemplate={SearchItemTemplate}
                options={options}
                onChange={(e: { value: ISearchResultTemplateProps | null }) =>
                  handleCaseSelect(e.value)
                }
                listStyle={{ height: '500px' }}
              />
              <div className='broad-search__separator' />
              <div className='broad-search__legend-area'>
                <div className='broad-search__legend-row'>
                  <Badge />
                  <Caption2>Cases</Caption2>
                </div>
              </div>
            </div>
          ) : (
            <div className='broad-search__results-container'>
              <div className='broad-search__no-results-box'>
                <h4 className='broad-search__no-results-heading'>
                  No Cases found
                </h4>
                <p className='broad-search__no-results-detail'>
                  {searchTerm && (
                    <>&#8220;{searchTerm}&#8221; returned no results</>
                  )}
                </p>
              </div>
            </div>
          )}
        </AnimateHeight>
      </div>
    </>
  );
};

export default BroadSearchAutoComplete;
